import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signOut as firebaseSignOut, 
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth, googleProvider } from '../apps/prayer-counter/firebase';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../apps/prayer-counter/firebase';
import { message } from 'antd';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const setupAuth = async () => {
      try {
        // Set persistence to local storage
        await setPersistence(auth, browserLocalPersistence);
        
        // Listen for authentication state changes
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          console.log('🔐 Auth state changed:', firebaseUser?.email || 'No user');
          
          if (firebaseUser) {
            // User is signed in
            await handleUserProfile(firebaseUser);
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              emailVerified: firebaseUser.emailVerified,
              metadata: firebaseUser.metadata
            });
          } else {
            // User is signed out
            setUser(null);
          }
          
          setLoading(false);
        });

        return unsubscribe;
      } catch (error) {
        console.error('❌ Error setting up auth:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    setupAuth();
  }, []);

  const handleUserProfile = async (firebaseUser) => {
    try {
      // Check if user profile exists
      const userProfileRef = doc(db, `users/${firebaseUser.uid}/profile`);
      const userProfileDoc = await getDoc(userProfileRef);
      
      if (!userProfileDoc.exists()) {
        // Create simple user profile from Google data
        console.log('📝 Creating user profile from Google data');
        
        await setDoc(userProfileRef, {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified,
          provider: 'google',
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          // Simple preferences - user can update these later
          preferences: {
            theme: 'saffron',
            notifications: true,
            language: 'en'
          }
        });
        
        console.log('✅ User profile created successfully');
      } else {
        // Update last login time and refresh Google data
        await setDoc(userProfileRef, {
          lastLoginAt: serverTimestamp(),
          // Keep Google account data up to date
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified
        }, { merge: true });
        
        console.log('✅ User profile updated');
      }
    } catch (error) {
      console.error('❌ Error handling user profile:', error);
      // Don't throw error - profile creation failure shouldn't block sign-in
      // This is especially important for new users
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 Starting Google Sign-In...');
      
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('✅ Google Sign-In successful:', user.email);
      message.success(`Welcome ${user.displayName || user.email}! 🙏`);
      
      return user;
    } catch (error) {
      console.error('❌ Google Sign-In error:', error);
      
      let errorMessage = 'Sign-in failed. Please try again.';
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many sign-in attempts. Please wait a moment and try again.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }
      
      setError(errorMessage);
      message.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      
      console.log('👋 Signing out...');
      await firebaseSignOut(auth);
      
      setUser(null);
      message.success('Signed out successfully');
      
      console.log('✅ Sign-out successful');
    } catch (error) {
      console.error('❌ Sign-out error:', error);
      message.error('Error signing out');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    error,
    signInWithGoogle,
    signOut,
    // Helper methods
    isAuthenticated: !!user,
    getUserId: () => user?.uid || null,
    getUserEmail: () => user?.email || null,
    getUserName: () => user?.displayName || user?.email || 'User',
    getUserPhoto: () => user?.photoURL || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 